/**
 * SchoolSystemSettings — the 18-section configuration backbone of the School OS.
 *
 * Strategy:
 *   • Extends existing endpoints (no breaks). Reads grouped sections from
 *     GET /api/school/tenants/{id}/settings/sections
 *   • Writes per-section via PUT /api/school/tenants/{id}/settings/section/{name}
 *   • Sections that have dedicated management pages (Timetable periods, Modules,
 *     Roles) show a compact summary + "Manage in …" link rather than duplicating
 *     the full editor.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CurrencySelect from '@/components/CurrencySelect';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Search, Save, Loader2, Settings as SettingsIcon, Building2, GraduationCap,
  FileText, Users as UsersIcon, Calendar, Clock, ClipboardCheck, BellRing,
  CreditCard, LayoutDashboard, PlayCircle, ScanLine, Palette, Shield, HardDrive,
  Database, Puzzle, Mail
} from 'lucide-react';
import { TenantAlertsConfig } from './TenantAlertsConfig';
import { TenantAutoBillingConfig } from './TenantAutoBillingConfig';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

// -------- Section registry (order + metadata drives both the sidebar TOC and search)

const SECTIONS = [
  { id: 'general', label: 'General', icon: SettingsIcon, keywords: 'school name code logo address phone email website country city timezone currency language favicon date time week language' },
  { id: 'tenant', label: 'Tenant / Institution', icon: Building2, keywords: 'tenant campus branch institution type active brand color' },
  { id: 'academic_session', label: 'Academic Session', icon: GraduationCap, keywords: 'session academic year term semester grading period promotion graduation cycle' },
  { id: 'admissions', label: 'Admissions', icon: FileText, keywords: 'admission applicant application number prefix digit required documents draft submitted approved workflow guardian parent auto link' },
  { id: 'staff', label: 'Staff', icon: UsersIcon, keywords: 'staff employee ID prefix department designation employment type restricted mode portal' },
  { id: 'attendance', label: 'Attendance', icon: Calendar, keywords: 'attendance day period biometric QR barcode online threshold late absent reason notification' },
  { id: 'timetable', label: 'Timetable', icon: Clock, keywords: 'timetable periods break lesson duration room class mode physical online hybrid conflict' },
  { id: 'exam_grading', label: 'Exams & Grading', icon: ClipboardCheck, keywords: 'exam pass marks grading GPA percentage rubric mode publish report ranking' },
  { id: 'notifications', label: 'Notifications', icon: BellRing, keywords: 'notification email SMS WhatsApp in-app reminder attendance exam assignment overdue parent' },
  { id: 'payments', label: 'Payments & Fees', icon: CreditCard, keywords: 'payment fee invoice numbering duplicate due days penalty late category gateway finance' },
  { id: 'portal', label: 'Portal', icon: LayoutDashboard, keywords: 'portal parent student teacher self-service profile editable widgets' },
  { id: 'lms', label: 'LMS / E-Learning', icon: PlayCircle, keywords: 'lms e-learning recorded lesson quiz completion catch-up recap visibility live class' },
  { id: 'id_access', label: 'ID & Access Control', icon: ScanLine, keywords: 'student id barcode QR card template restriction access log security scan entry exit' },
  { id: 'cms_branding', label: 'CMS / Website / Branding', icon: Palette, keywords: 'theme primary secondary color front CMS announcement print header footer email branding' },
  { id: 'roles_permissions', label: 'Roles & Permissions', icon: Shield, keywords: 'roles permissions matrix module access tenant admin custom sensitive' },
  { id: 'files', label: 'Files & Uploads', icon: HardDrive, keywords: 'file types upload size document image category student assignment media storage' },
  { id: 'maintenance', label: 'Backup & Maintenance', icon: Database, keywords: 'backup restore system update diagnostic logs audit retention' },
  { id: 'modules', label: 'Module Management', icon: Puzzle, keywords: 'enable disable modules per tenant visibility school type default bundle boarding finance' },
  { id: 'email', label: 'Email / SMTP', icon: Mail, keywords: 'email SMTP host port user password from reply to server' },
];

// -------- Small reusable form primitives (keep file under control)

const Row = ({ children, cols = 2 }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-3`}>{children}</div>
);

const Field = ({ label, hint, children, testId }) => (
  <div data-testid={testId}>
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);

const ToggleRow = ({ label, description, value, onChange, testId }) => (
  <div className="flex items-center justify-between py-2" data-testid={testId}>
    <div className="flex-1 pr-3">
      <p className="text-sm font-medium text-slate-900">{label}</p>
      {description && <p className="text-xs text-slate-500">{description}</p>}
    </div>
    <Switch checked={!!value} onCheckedChange={onChange} />
  </div>
);

const SaveBar = ({ onSave, isSaving, testId }) => (
  <div className="flex justify-end pt-2 border-t mt-4">
    <Button size="sm" onClick={onSave} disabled={isSaving} data-testid={testId}>
      {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Save className="h-3.5 w-3.5 mr-2" />}
      Save
    </Button>
  </div>
);

// -------- Main component --------

export const SchoolSystemSettings = ({ tenantId }) => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showAudit, setShowAudit] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [cloneSource, setCloneSource] = useState('');
  const [cloneOverwrite, setCloneOverwrite] = useState(false);

  const { data: sections, isLoading } = useQuery({
    queryKey: ['system-settings', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/settings/sections`),
    enabled: !!tenantId,
  });

  const { data: otherTenants = [] } = useQuery({
    queryKey: ['school-tenants-for-clone'],
    queryFn: () => api('/tenants'),
  });

  const { data: audit = [] } = useQuery({
    queryKey: ['settings-audit', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/settings/audit?limit=20`),
    enabled: !!tenantId && showAudit,
  });

  const { data: health } = useQuery({
    queryKey: ['settings-health', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/settings/health`),
    enabled: !!tenantId,
  });

  const saveSection = useMutation({
    mutationFn: ({ section, data }) => api(`/tenants/${tenantId}/settings/section/${section}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: (_d, vars) => {
      toast.success(`${SECTIONS.find(s => s.id === vars.section)?.label || 'Settings'} saved`);
      qc.invalidateQueries({ queryKey: ['system-settings', tenantId] });
      qc.invalidateQueries({ queryKey: ['school-tenant', tenantId] });
      qc.invalidateQueries({ queryKey: ['school-tenants'] });
      qc.invalidateQueries({ queryKey: ['settings-audit', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const cloneFrom = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/settings/clone-from/${cloneSource}?overwrite=${cloneOverwrite}`, { method: 'POST' }),
    onSuccess: (data) => {
      toast.success(`Imported ${data.count} setting groups`);
      setShowClone(false); setCloneSource(''); setCloneOverwrite(false);
      qc.invalidateQueries({ queryKey: ['system-settings', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const visibleSections = useMemo(() => {
    if (!search.trim()) return SECTIONS;
    const q = search.trim().toLowerCase();
    return SECTIONS.filter(s => s.label.toLowerCase().includes(q) || s.keywords.toLowerCase().includes(q));
  }, [search]);

  if (isLoading || !sections) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>;
  }

  const doSave = (section, data) => saveSection.mutate({ section, data });

  return (
    <div className="space-y-4" data-testid="school-system-settings">
      {/* Real-time Alerts — tenant-scoped webhook preferences */}
      <TenantAlertsConfig tenantId={tenantId} />

      {/* Auto-Billing — Housing + Dining → pending fee invoices */}
      <TenantAutoBillingConfig tenantId={tenantId} />

      {/* Health check banner */}
      {health && health.checks?.length > 0 && (
        <Card className={health.warnings > 0 ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'} data-testid="health-banner">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${health.score >= 90 ? 'bg-emerald-500' : health.score >= 70 ? 'bg-amber-500' : 'bg-red-500'} text-white font-bold text-sm`}>
                {health.score}
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Settings Health: {health.score}/100</p>
                <p className="text-xs text-slate-600 mt-0.5">{health.warnings} warnings · {health.infos} suggestions</p>
                <ul className="mt-2 space-y-0.5 text-xs text-slate-700">
                  {health.checks.slice(0, 5).map((c, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${c.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'}`}></span>
                      <span><Badge variant="outline" className="text-[10px] mr-1">{c.section}</Badge>{c.message}</span>
                    </li>
                  ))}
                  {health.checks.length > 5 && <li className="text-[11px] text-slate-500 ml-3.5">+{health.checks.length - 5} more…</li>}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top toolbar: search + import + audit */}
      <div className="sticky top-0 z-10 bg-white pb-2 space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search settings — e.g. late, prefix, portal, SMTP, invoice…"
              className="pl-9"
              data-testid="settings-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowClone(true)} data-testid="clone-settings-btn" className="shrink-0">
            <Puzzle className="h-3.5 w-3.5 mr-1.5" /> Import
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowAudit(v => !v)} data-testid="view-audit-btn" className="shrink-0">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Audit
          </Button>
        </div>
        {search && visibleSections.length === 0 && (
          <p className="text-sm text-slate-500">No sections matched “{search}”.</p>
        )}
      </div>

      {/* Clone settings banner */}
      {showClone && (
        <Card className="border-amber-200 bg-amber-50" data-testid="clone-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-slate-900">Import settings from another school</p>
                <p className="text-xs text-slate-600 mt-0.5">Copies all configuration (excluding name/code/contact details). Safe-merge only fills empty fields unless Overwrite is ticked.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowClone(false)}>✕</Button>
            </div>
            <Row cols={2}>
              <Field label="Source School">
                <Select value={cloneSource} onValueChange={setCloneSource}>
                  <SelectTrigger><SelectValue placeholder="Select source…" /></SelectTrigger>
                  <SelectContent>
                    {(otherTenants || []).filter(t => t.id !== tenantId).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} ({t.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={cloneOverwrite} onChange={e => setCloneOverwrite(e.target.checked)} />
                  Overwrite existing values
                </label>
              </div>
            </Row>
            <div className="flex justify-end">
              <Button size="sm" disabled={!cloneSource || cloneFrom.isPending} onClick={() => cloneFrom.mutate()} data-testid="clone-submit-btn">
                {cloneFrom.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Save className="h-3.5 w-3.5 mr-2" />}
                Import
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit log */}
      {showAudit && (
        <Card data-testid="audit-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Recent Changes</CardTitle>
            <CardDescription className="text-xs">Last 20 settings-change events for this school.</CardDescription>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            {audit.length === 0 ? (
              <p className="text-xs text-slate-400 py-6 text-center">No settings changes yet.</p>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {audit.map(e => (
                  <div key={e.id} className="flex items-center justify-between text-xs border-b py-1.5 last:border-0">
                    <div>
                      <Badge variant="outline" className="text-[10px] mr-2">{e.section}</Badge>
                      <span className="text-slate-600">{(e.payload_keys || []).slice(0, 4).join(', ')}{(e.payload_keys || []).length > 4 && '…'}</span>
                    </div>
                    <div className="text-slate-400">
                      {e.changed_by} · {e.changed_at ? new Date(e.changed_at).toLocaleString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Accordion type="multiple" className="space-y-2" defaultValue={['general']}>
        {visibleSections.map(s => {
          const Icon = s.icon;
          return (
            <AccordionItem key={s.id} value={s.id} className="border rounded-lg bg-white px-3" data-testid={`section-${s.id}`}>
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-md bg-indigo-50 flex items-center justify-center">
                    <Icon className="h-4 w-4 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-slate-900">{s.label}</p>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-1 pb-4">
                <SectionForm
                  sectionId={s.id}
                  data={sections[s.id] || {}}
                  onSave={(d) => doSave(s.id, d)}
                  isSaving={saveSection.isPending && saveSection.variables?.section === s.id}
                  allModules={sections.modules?.available || []}
                  tenantId={tenantId}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

// -------- Per-section forms (kept compact; complex sections link out to dedicated editors) --------

function SectionForm({ sectionId, data, onSave, isSaving, allModules, tenantId }) {
  const [form, setForm] = useState(() => ({ ...data }));
  React.useEffect(() => { setForm({ ...data }); }, [data]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const saveBtn = <SaveBar onSave={() => onSave(form)} isSaving={isSaving} testId={`save-${sectionId}`} />;

  switch (sectionId) {
    case 'general':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="School Name"><Input value={form.school_name || ''} onChange={e => set('school_name', e.target.value)} data-testid="field-school_name" /></Field>
            <Field label="School Code"><Input value={form.school_code || ''} onChange={e => set('school_code', e.target.value.toUpperCase())} data-testid="field-school_code" /></Field>
            <Field label="Logo" hint="Full logo used in headers / emails"><MediaUpload value={form.logo_url || ''} onChange={(url) => set('logo_url', url)} purpose="logo" testId="field-logo_url" previewClassName="h-16" /></Field>
            <Field label="Favicon / small logo"><MediaUpload value={form.favicon_url || ''} onChange={(url) => set('favicon_url', url)} purpose="favicon" testId="field-favicon_url" previewClassName="h-12" /></Field>
            <Field label="Address"><Input value={form.address || ''} onChange={e => set('address', e.target.value)} /></Field>
            <Field label="Phone"><Input value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></Field>
            <Field label="Email"><Input value={form.email || ''} onChange={e => set('email', e.target.value)} /></Field>
            <Field label="Website"><Input value={form.website || ''} onChange={e => set('website', e.target.value)} placeholder="https://…" /></Field>
            <Field label="Country"><Input value={form.country || ''} onChange={e => set('country', e.target.value)} /></Field>
            <Field label="City"><Input value={form.city || ''} onChange={e => set('city', e.target.value)} /></Field>
            <Field label="Timezone" hint="e.g. Africa/Harare"><Input value={form.timezone || ''} onChange={e => set('timezone', e.target.value)} /></Field>
            <Field label="Language">
              <Select value={form.language || 'en'} onValueChange={v => set('language', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="sn">Shona</SelectItem>
                  <SelectItem value="nd">Ndebele</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="pt">Portuguese</SelectItem>
                </SelectContent></Select>
            </Field>
            <Field label="Currency">
              <CurrencySelect
                value={form.currency || 'USD'}
                onChange={v => set('currency', v)}
                showHomeHint={false}
                testId="school-currency"
              />
            </Field>
            <Field label="Currency Symbol"><Input value={form.currency_symbol || '$'} onChange={e => set('currency_symbol', e.target.value)} /></Field>
            <Field label="Date Format">
              <Select value={form.date_format || 'DD/MM/YYYY'} onValueChange={v => set('date_format', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent></Select>
            </Field>
            <Field label="Time Format">
              <Select value={form.time_format || '24h'} onValueChange={v => set('time_format', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">24-hour</SelectItem>
                  <SelectItem value="12h">12-hour</SelectItem>
                </SelectContent></Select>
            </Field>
            <Field label="Week Starts On">
              <Select value={form.week_start_day || 'monday'} onValueChange={v => set('week_start_day', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['sunday', 'monday', 'saturday'].map(d => <SelectItem key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</SelectItem>)}
                </SelectContent></Select>
            </Field>
          </Row>
          {saveBtn}
        </div>
      );

    case 'tenant':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Tenant Name"><Input value={form.name || ''} onChange={e => set('name', e.target.value)} /></Field>
            <Field label="Campus / Branch Name"><Input value={form.branch_name || ''} onChange={e => set('branch_name', e.target.value)} placeholder="e.g. Main Campus" /></Field>
            <Field label="Code"><Input value={form.code || ''} onChange={e => set('code', e.target.value.toUpperCase())} /></Field>
            <Field label="Institution Type">
              <Select value={form.type || 'school'} onValueChange={v => set('type', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['school', 'college', 'institute', 'campus', 'university'].map(t => <SelectItem key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</SelectItem>)}
                </SelectContent></Select>
            </Field>
            <Field label="Primary Brand Color">
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color || '#6366f1'} onChange={e => set('primary_color', e.target.value)} className="h-9 w-12 rounded border cursor-pointer" />
                <Input value={form.primary_color || '#6366f1'} onChange={e => set('primary_color', e.target.value)} className="flex-1" />
              </div>
            </Field>
            <Field label="Secondary Brand Color">
              <div className="flex items-center gap-2">
                <input type="color" value={form.secondary_color || '#f59e0b'} onChange={e => set('secondary_color', e.target.value)} className="h-9 w-12 rounded border cursor-pointer" />
                <Input value={form.secondary_color || '#f59e0b'} onChange={e => set('secondary_color', e.target.value)} className="flex-1" />
              </div>
            </Field>
          </Row>
          <ToggleRow label="Active" description="Inactive tenants cannot be accessed by their users" value={form.is_active !== false} onChange={v => set('is_active', v)} />
          {saveBtn}
        </div>
      );

    case 'academic_session':
      return (
        <div className="space-y-3">
          <div className="bg-slate-50 border rounded-md p-3 text-sm">
            <p className="font-medium">Current Year: <span className="text-indigo-700">{form.current_year_name || 'Not set'}</span></p>
            <p className="text-slate-500 text-xs">Current Term: {form.current_term_name || 'Not set'}</p>
            <p className="text-xs text-slate-500 mt-2">Manage academic years & terms in the <strong>Academic Years</strong> tab on the sidebar.</p>
          </div>
          <Row cols={2}>
            <Field label="Session Start Month">
              <Select value={String(form.session_start_month || '1')} onValueChange={v => set('session_start_month', Number(v))}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent></Select>
            </Field>
            <Field label="Grading Period Structure">
              <Select value={form.grading_period || 'term'} onValueChange={v => set('grading_period', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="term">3 Terms</SelectItem>
                  <SelectItem value="semester">2 Semesters</SelectItem>
                  <SelectItem value="quarter">4 Quarters</SelectItem>
                </SelectContent></Select>
            </Field>
          </Row>
          <ToggleRow label="Auto-promote on year end" description="When the academic year closes, eligible students advance automatically." value={form.auto_promote} onChange={v => set('auto_promote', v)} />
          <ToggleRow label="Graduation stage for senior class" value={form.auto_graduate_senior} onChange={v => set('auto_graduate_senior', v)} />
          {saveBtn}
        </div>
      );

    case 'admissions':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Number Prefix"><Input value={form.auto_number_prefix || 'ADM'} onChange={e => set('auto_number_prefix', e.target.value.toUpperCase())} /></Field>
            <Field label="Start Number"><Input type="number" value={form.start_number || 1001} onChange={e => set('start_number', Number(e.target.value))} /></Field>
            <Field label="Digit Length"><Input type="number" value={form.digit_length || 5} onChange={e => set('digit_length', Number(e.target.value))} /></Field>
          </Row>
          <Field label="Required Documents (comma-separated)">
            <Textarea rows={2} value={(form.required_documents || []).join(', ')} onChange={e => set('required_documents', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
          </Field>
          <Field label="Workflow Steps (comma-separated)">
            <Input value={(form.workflow_steps || []).join(', ')} onChange={e => set('workflow_steps', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
          </Field>
          <ToggleRow label="Online Admissions Enabled" description="Let applicants submit applications from the public site" value={form.online_enabled !== false} onChange={v => set('online_enabled', v)} />
          <ToggleRow label="Auto-link Parent Account" description="Create a parent/guardian portal login when an applicant is approved" value={form.auto_link_parent !== false} onChange={v => set('auto_link_parent', v)} />
          {saveBtn}
        </div>
      );

    case 'staff':
      return (
        <div className="space-y-3">
          <Row cols={3}>
            <Field label="Staff ID Prefix"><Input value={form.auto_id_prefix || 'STF'} onChange={e => set('auto_id_prefix', e.target.value.toUpperCase())} /></Field>
            <Field label="Start Number"><Input type="number" value={form.start_number || 1001} onChange={e => set('start_number', Number(e.target.value))} /></Field>
            <Field label="Digit Length"><Input type="number" value={form.digit_length || 5} onChange={e => set('digit_length', Number(e.target.value))} /></Field>
          </Row>
          <Field label="Departments (comma-separated)">
            <Textarea rows={2} value={(form.departments || []).join(', ')} onChange={e => set('departments', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
          </Field>
          <Field label="Designations (comma-separated)">
            <Textarea rows={2} value={(form.designations || []).join(', ')} onChange={e => set('designations', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
          </Field>
          <Field label="Employment Types (comma-separated)">
            <Input value={(form.employment_types || []).join(', ')} onChange={e => set('employment_types', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
          </Field>
          <ToggleRow label="Teacher Restricted Mode" description="Teachers see only their own classes and students" value={form.restricted_mode} onChange={v => set('restricted_mode', v)} />
          {saveBtn}
        </div>
      );

    case 'attendance':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Mode">
              <Select value={form.mode || 'day'} onValueChange={v => set('mode', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day-wise (once per day)</SelectItem>
                  <SelectItem value="period">Period-wise (every class)</SelectItem>
                </SelectContent></Select>
            </Field>
            <Field label="Minimum Attendance %"><Input type="number" value={form.min_attendance_percent ?? 80} onChange={e => set('min_attendance_percent', Number(e.target.value))} /></Field>
            <Field label="Late Threshold (minutes)"><Input type="number" value={form.late_threshold_minutes ?? 15} onChange={e => set('late_threshold_minutes', Number(e.target.value))} /></Field>
            <Field label="Mark Absent After (minutes)"><Input type="number" value={form.absent_after_minutes ?? 30} onChange={e => set('absent_after_minutes', Number(e.target.value))} /></Field>
          </Row>
          <ToggleRow label="Biometric Attendance" value={form.biometric_enabled} onChange={v => set('biometric_enabled', v)} />
          <ToggleRow label="QR / Barcode Scan Attendance" value={form.qr_enabled !== false} onChange={v => set('qr_enabled', v)} />
          <ToggleRow label="Require Absence Reason" value={form.require_absence_reason} onChange={v => set('require_absence_reason', v)} />
          <ToggleRow label="Auto-Notify Parent on Absence" value={form.auto_notify_parent !== false} onChange={v => set('auto_notify_parent', v)} />
          {saveBtn}
        </div>
      );

    case 'timetable':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Periods per Day"><Input type="number" value={form.periods_per_day ?? 8} onChange={e => set('periods_per_day', Number(e.target.value))} /></Field>
            <Field label="Lesson Duration (minutes)"><Input type="number" value={form.lesson_duration_minutes ?? 45} onChange={e => set('lesson_duration_minutes', Number(e.target.value))} /></Field>
            <Field label="Default Class Mode">
              <Select value={form.class_mode_default || 'physical'} onValueChange={v => set('class_mode_default', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical">Physical</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent></Select>
            </Field>
            <Field label="Days of Week (comma-separated)">
              <Input value={(form.days_of_week || []).join(', ')} onChange={e => set('days_of_week', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Mon, Tue, Wed, Thu, Fri" />
            </Field>
          </Row>
          <ToggleRow label="Detect Timetable Conflicts" description="Prevent overlapping teacher/room assignments" value={form.conflict_detection !== false} onChange={v => set('conflict_detection', v)} />
          <p className="text-xs text-slate-500 mt-2">Manage period timings and break blocks in the <strong>Timetable</strong> module.</p>
          {saveBtn}
        </div>
      );

    case 'exam_grading':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Default Pass Mark (%)"><Input type="number" value={form.default_pass_mark ?? 50} onChange={e => set('default_pass_mark', Number(e.target.value))} /></Field>
            <Field label="Mark Entry Deadline (days)"><Input type="number" value={form.mark_entry_deadline_days ?? 7} onChange={e => set('mark_entry_deadline_days', Number(e.target.value))} /></Field>
          </Row>
          <ToggleRow label="GPA Enabled" value={form.gpa_enabled !== false} onChange={v => set('gpa_enabled', v)} />
          <ToggleRow label="Rubric-based Grading" value={form.rubrics_enabled !== false} onChange={v => set('rubrics_enabled', v)} />
          <ToggleRow label="Class Ranking Enabled" value={form.ranking_enabled !== false} onChange={v => set('ranking_enabled', v)} />
          <ToggleRow label="Publish Report Card on Approval" value={form.publish_on_approval !== false} onChange={v => set('publish_on_approval', v)} />
          <Field
            label="Exam Fee-Gate Mode"
            hint="What happens when a student with outstanding fees tries to start an exam. 'Block' is the safest default; 'Warn' lets them sit while flagging the debt; 'Allow' disables the gate entirely."
          >
            <select
              data-testid="exam-fee-mode-select"
              className="w-full border rounded h-9 px-2 text-sm bg-white"
              value={form.fee_mode || 'block'}
              onChange={e => set('fee_mode', e.target.value)}
            >
              <option value="block">Block — no start with outstanding fees</option>
              <option value="warn">Warn — show notice but allow start</option>
              <option value="allow">Allow — ignore fees</option>
            </select>
          </Field>
          <GradingScaleEditor tenantId={tenantId} />
          {saveBtn}
        </div>
      );

    case 'notifications':
      return (
        <div className="space-y-3">
          <ToggleRow label="Global Notifications" description="Master switch for every outbound alert" value={form.global_enabled !== false} onChange={v => set('global_enabled', v)} />
          <Row cols={2}>
            <ToggleRow label="Email" value={form.email_enabled !== false} onChange={v => set('email_enabled', v)} />
            <ToggleRow label="SMS" value={form.sms_enabled} onChange={v => set('sms_enabled', v)} />
            <ToggleRow label="WhatsApp" value={form.whatsapp_enabled} onChange={v => set('whatsapp_enabled', v)} />
            <ToggleRow label="In-app" value={form.inapp_enabled !== false} onChange={v => set('inapp_enabled', v)} />
          </Row>
          <Row cols={2}>
            <ToggleRow label="Attendance Alerts" value={form.attendance_alerts !== false} onChange={v => set('attendance_alerts', v)} />
            <ToggleRow label="Exam Alerts" value={form.exam_alerts !== false} onChange={v => set('exam_alerts', v)} />
            <ToggleRow label="Assignment Alerts" value={form.assignment_alerts !== false} onChange={v => set('assignment_alerts', v)} />
            <ToggleRow label="Overdue Fee Notices" value={form.overdue_notices !== false} onChange={v => set('overdue_notices', v)} />
          </Row>
          <Field label="Reminder Hours Before Deadline"><Input type="number" value={form.reminder_hours_before ?? 24} onChange={e => set('reminder_hours_before', Number(e.target.value))} /></Field>
          {saveBtn}
        </div>
      );

    case 'payments':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Invoice Prefix"><Input value={form.invoice_prefix || 'INV'} onChange={e => set('invoice_prefix', e.target.value.toUpperCase())} /></Field>
            <Field label="Invoice Start Number"><Input type="number" value={form.invoice_start_number ?? 1001} onChange={e => set('invoice_start_number', Number(e.target.value))} /></Field>
            <Field label="Due Days"><Input type="number" value={form.due_days ?? 30} onChange={e => set('due_days', Number(e.target.value))} /></Field>
            <Field label="Penalty %"><Input type="number" step="0.1" value={form.penalty_percent ?? 5} onChange={e => set('penalty_percent', Number(e.target.value))} /></Field>
            <Field label="Late Fee (fixed)"><Input type="number" step="0.01" value={form.late_fee_fixed ?? 0} onChange={e => set('late_fee_fixed', Number(e.target.value))} /></Field>
          </Row>
          <Field label="Accepted Gateways (comma-separated)">
            <Input value={(form.gateways || []).join(', ')} onChange={e => set('gateways', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="stripe, paypal, ecocash, bank_transfer" />
          </Field>
          <ToggleRow label="Full Finance Module" description="Unlocks the finance/accounting dashboard" value={form.finance_module_enabled} onChange={v => set('finance_module_enabled', v)} />
          {saveBtn}
        </div>
      );

    case 'portal':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <ToggleRow label="Student Portal" value={form.student_portal_enabled !== false} onChange={v => set('student_portal_enabled', v)} />
            <ToggleRow label="Parent Portal" value={form.parent_portal_enabled !== false} onChange={v => set('parent_portal_enabled', v)} />
            <ToggleRow label="Teacher Portal" value={form.teacher_portal_enabled !== false} onChange={v => set('teacher_portal_enabled', v)} />
            <ToggleRow label="Allow Self-service Profile Edits" value={form.self_service_profile} onChange={v => set('self_service_profile', v)} />
          </Row>
          <Field label="Fields users can edit (comma-separated)">
            <Input value={(form.editable_fields || []).join(', ')} onChange={e => set('editable_fields', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
          </Field>
          {saveBtn}
        </div>
      );

    case 'lms':
      return (
        <div className="space-y-3">
          <ToggleRow label="LMS Enabled" value={form.enabled !== false} onChange={v => set('enabled', v)} />
          <ToggleRow label="Recorded Lessons" value={form.recorded_lessons_enabled !== false} onChange={v => set('recorded_lessons_enabled', v)} />
          <ToggleRow label="Recap Quizzes" value={form.recap_quiz_enabled !== false} onChange={v => set('recap_quiz_enabled', v)} />
          <Row cols={2}>
            <Field label="Default Quiz Time Limit (min)"><Input type="number" value={form.quiz_time_limit_default ?? 30} onChange={e => set('quiz_time_limit_default', Number(e.target.value))} /></Field>
            <Field label="Catch-up Window (days)"><Input type="number" value={form.catchup_window_days ?? 14} onChange={e => set('catchup_window_days', Number(e.target.value))} /></Field>
            <Field label="Lesson Completion Rule">
              <Select value={form.completion_rule || 'watch_100_percent'} onValueChange={v => set('completion_rule', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="watch_100_percent">Watch 100%</SelectItem>
                  <SelectItem value="watch_80_percent">Watch 80%</SelectItem>
                  <SelectItem value="quiz_pass">Pass Quiz</SelectItem>
                  <SelectItem value="manual_mark">Manual Mark</SelectItem>
                </SelectContent></Select>
            </Field>
            <Field label="Live Class Provider">
              <Select value={form.provider || 'zoom'} onValueChange={v => set('provider', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="meet">Google Meet</SelectItem>
                  <SelectItem value="teams">Microsoft Teams</SelectItem>
                  <SelectItem value="jitsi">Jitsi</SelectItem>
                </SelectContent></Select>
            </Field>
          </Row>
          {saveBtn}
        </div>
      );

    case 'id_access':
      return (
        <div className="space-y-3">
          <Row cols={3}>
            <Field label="Student ID Prefix"><Input value={form.prefix || 'STU'} onChange={e => set('prefix', e.target.value.toUpperCase())} /></Field>
            <Field label="Start Number"><Input type="number" value={form.start_number ?? 1001} onChange={e => set('start_number', Number(e.target.value))} /></Field>
            <Field label="Digit Length"><Input type="number" value={form.digit_length ?? 5} onChange={e => set('digit_length', Number(e.target.value))} /></Field>
          </Row>
          <ToggleRow label="Auto-generate Student IDs" value={form.auto_student_id !== false} onChange={v => set('auto_student_id', v)} />
          <ToggleRow label="QR Codes on ID Cards" value={form.qr_enabled !== false} onChange={v => set('qr_enabled', v)} />
          <ToggleRow label="Barcodes on ID Cards" value={form.barcode_enabled} onChange={v => set('barcode_enabled', v)} />
          <ToggleRow label="Log every scan" value={form.access_logging !== false} onChange={v => set('access_logging', v)} />
          <p className="text-xs text-slate-500 mt-2">ID card template / time rules are edited in the <strong>ID & Access Control</strong> module.</p>
          {saveBtn}
        </div>
      );

    case 'cms_branding':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Theme">
              <Select value={form.theme || 'default'} onValueChange={v => set('theme', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                </SelectContent></Select>
            </Field>
            <Field label="Primary Color">
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color || '#6366f1'} onChange={e => set('primary_color', e.target.value)} className="h-9 w-12 rounded border cursor-pointer" />
                <Input value={form.primary_color || '#6366f1'} onChange={e => set('primary_color', e.target.value)} className="flex-1" />
              </div>
            </Field>
          </Row>
          <Field label="Print Header (appears on reports & certificates)"><Textarea rows={2} value={form.print_header || ''} onChange={e => set('print_header', e.target.value)} /></Field>
          <Field label="Print Footer"><Textarea rows={2} value={form.print_footer || ''} onChange={e => set('print_footer', e.target.value)} /></Field>
          <ToggleRow label="Announcement Board on Portal" value={form.announcement_board_visible !== false} onChange={v => set('announcement_board_visible', v)} />
          {saveBtn}
        </div>
      );

    case 'modules':
      return (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Tick the modules this school should have access to. Disabling hides them from sidebars and blocks their API endpoints.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {(allModules || []).map(m => {
              const enabled = (form.enabled_modules || []).includes(m);
              return (
                <label key={m} className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer transition ${enabled ? 'bg-indigo-50 border-indigo-200' : 'hover:bg-slate-50'}`}>
                  <input type="checkbox" checked={enabled} onChange={e => {
                    const cur = new Set(form.enabled_modules || []);
                    if (e.target.checked) cur.add(m); else cur.delete(m);
                    set('enabled_modules', [...cur]);
                  }} />
                  <span className="text-sm capitalize">{m.replace(/_/g, ' ')}</span>
                </label>
              );
            })}
          </div>
          {saveBtn}
        </div>
      );

    // --- stub sections (functional but compact; link to dedicated editors where relevant) ---
    case 'roles_permissions':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Default Role for New Staff">
              <Select value={form.default_role || 'teacher'} onValueChange={v => set('default_role', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['admin', 'principal', 'teacher', 'accountant', 'librarian', 'front_office'].map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}
                </SelectContent></Select>
            </Field>
          </Row>
          <ToggleRow label="Require 2FA for Sensitive Actions" description="Fee adjustments, bulk deletions, role changes" value={form.sensitive_actions_require_2fa} onChange={v => set('sensitive_actions_require_2fa', v)} />
          <p className="text-xs text-slate-500">Edit roles and the permission matrix in the <strong>Team & Roles</strong> tab.</p>
          {saveBtn}
        </div>
      );

    case 'files':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="Max Upload Size (MB)"><Input type="number" value={form.max_upload_mb ?? 10} onChange={e => set('max_upload_mb', Number(e.target.value))} /></Field>
            <Field label="Allowed File Types (comma-separated)"><Input value={(form.allowed_file_types || []).join(', ')} onChange={e => set('allowed_file_types', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} /></Field>
          </Row>
          <Field label="Document Categories (comma-separated)">
            <Input value={(form.document_categories || []).join(', ')} onChange={e => set('document_categories', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} />
          </Field>
          {saveBtn}
        </div>
      );

    case 'maintenance':
      return (
        <div className="space-y-3">
          <ToggleRow label="Scheduled Backups" value={form.backup_enabled !== false} onChange={v => set('backup_enabled', v)} />
          <ToggleRow label="Audit Logging" description="Record who changed what (recommended)" value={form.audit_logging !== false} onChange={v => set('audit_logging', v)} />
          <Row cols={2}>
            <Field label="Backup Frequency">
              <Select value={form.backup_frequency || 'daily'} onValueChange={v => set('backup_frequency', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent></Select>
            </Field>
            <Field label="Retention (days)"><Input type="number" value={form.retention_days ?? 365} onChange={e => set('retention_days', Number(e.target.value))} /></Field>
          </Row>
          <Badge variant="outline" className="text-[10px]">Backup scheduling infrastructure is managed at the platform layer. Request changes via support if needed.</Badge>
          {saveBtn}
        </div>
      );

    case 'email':
      return (
        <div className="space-y-3">
          <Row cols={2}>
            <Field label="SMTP Host"><Input value={form.smtp_host || ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.gmail.com" /></Field>
            <Field label="SMTP Port"><Input type="number" value={form.smtp_port ?? 587} onChange={e => set('smtp_port', Number(e.target.value))} /></Field>
            <Field label="SMTP Username"><Input value={form.smtp_user || ''} onChange={e => set('smtp_user', e.target.value)} placeholder="you@gmail.com" /></Field>
            <Field label="SMTP Password (Gmail App Password)">
              <Input
                type="password"
                value={form.smtp_password || ''}
                onChange={e => set('smtp_password', e.target.value)}
                placeholder="Leave blank to keep existing"
                autoComplete="new-password"
                data-testid="school-system-smtp-password"
              />
            </Field>
            <Field label="From Email"><Input value={form.from_email || ''} onChange={e => set('from_email', e.target.value)} /></Field>
            <Field label="From Name"><Input value={form.from_name || ''} onChange={e => set('from_name', e.target.value)} /></Field>
          </Row>
          <p className="text-xs text-slate-500">
            For Gmail you need a 16-character <strong>App Password</strong> — create one at{' '}
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline text-blue-600">myaccount.google.com/apppasswords</a>{' '}
            (requires 2-Step Verification on the account). To send a test email and toggle per-school SMTP on/off, use the <strong>Email Settings</strong> tab on the sidebar.
          </p>
          {saveBtn}
        </div>
      );

    default:
      return <p className="text-sm text-slate-500">No editor configured for this section yet.</p>;
  }
}

export default SchoolSystemSettings;

// ============== GRADING SCALE EDITOR ==============
// Lives at the bottom of the file so the Settings tree stays a single import.
// Adds/edits/removes grade bands; persists via PUT /tenants/{tid}/grading-scale.
function GradingScaleEditor({ tenantId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['grading-scale', tenantId],
    queryFn: () => fetch(`${API_URL}/api/school/tenants/${tenantId}/grading-scale`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(r => r.json()),
    enabled: !!tenantId,
  });
  if (isLoading) return <div className="text-xs text-slate-400">Loading grading scale…</div>;
  if (!data) return null;
  // Remount on every fetch so internal edit-state stays in sync with the server
  // without leaning on set-state-in-effect.
  return <GradingScaleEditorInner key={`gs-${data.pass_mark}-${(data.scale || []).length}`} tenantId={tenantId} initial={data} qc={qc} />;
}

function GradingScaleEditorInner({ tenantId, initial, qc }) {
  const [editing, setEditing] = React.useState(false);
  const [bands, setBands] = React.useState(() => (initial?.scale || []).map(b => ({ ...b })));
  const [passMark, setPassMark] = React.useState(() => initial?.pass_mark ?? 50);

  const save = useMutation({
    mutationFn: () => fetch(`${API_URL}/api/school/tenants/${tenantId}/grading-scale`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ scale: bands, pass_mark: passMark, ranking_enabled: true, gpa_enabled: true }),
    }).then(async (r) => {
      if (!r.ok) throw new Error((await r.json()).detail || 'Save failed');
      return r.json();
    }),
    onSuccess: () => {
      toast.success('Grading scale saved');
      qc.invalidateQueries({ queryKey: ['grading-scale'] });
      setEditing(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateBand = (i, field, value) => {
    setBands((prev) => prev.map((b, idx) => idx === i ? { ...b, [field]: value } : b));
  };
  const removeBand = (i) => setBands((prev) => prev.filter((_, idx) => idx !== i));
  const addBand = () => setBands((prev) => [...prev, { grade: '', min: 0, max: 100, gpa: 0, classification: 'pass', remark: '' }]);

  return (
    <div className="border rounded-lg p-3 mt-3" data-testid="grading-scale-editor">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-semibold">Grading Scale</p>
          <p className="text-xs text-slate-500">Configure grade bands · pass mark · classification per band.</p>
        </div>
        {!editing ? (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="grading-edit-btn">Edit</Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setBands((initial?.scale || []).map(b => ({ ...b }))); setPassMark(initial?.pass_mark ?? 50); }}>Cancel</Button>
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending} data-testid="grading-save-btn">
              {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save scale'}
            </Button>
          </div>
        )}
      </div>
      {editing && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-slate-500">Pass mark</span>
          <Input type="number" value={passMark} onChange={(e) => setPassMark(Number(e.target.value))} className="w-20 h-8" data-testid="grading-pass-mark" />
          <span className="text-xs text-slate-500">%</span>
        </div>
      )}
      <div className="grid grid-cols-1 gap-1.5">
        <div className="grid grid-cols-12 gap-2 text-[11px] text-slate-400 px-1">
          <span className="col-span-1">Grade</span>
          <span className="col-span-2">Min %</span>
          <span className="col-span-2">Max %</span>
          <span className="col-span-1">GPA</span>
          <span className="col-span-2">Class.</span>
          <span className="col-span-3">Remark</span>
          <span className="col-span-1"></span>
        </div>
        {bands.map((b, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`grading-band-${i}`}>
            <Input className="col-span-1 h-8" disabled={!editing} value={b.grade || ''} onChange={(e) => updateBand(i, 'grade', e.target.value)} />
            <Input className="col-span-2 h-8" disabled={!editing} type="number" value={b.min ?? 0} onChange={(e) => updateBand(i, 'min', Number(e.target.value))} />
            <Input className="col-span-2 h-8" disabled={!editing} type="number" value={b.max ?? 100} onChange={(e) => updateBand(i, 'max', Number(e.target.value))} />
            <Input className="col-span-1 h-8" disabled={!editing} type="number" step="0.1" value={b.gpa ?? 0} onChange={(e) => updateBand(i, 'gpa', Number(e.target.value))} />
            {editing ? (
              <Select value={b.classification || 'pass'} onValueChange={(v) => updateBand(i, 'classification', v)}>
                <SelectTrigger className="col-span-2 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['distinction', 'merit', 'credit', 'pass', 'fail'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <span className="col-span-2 text-xs capitalize text-slate-600">{b.classification || 'pass'}</span>
            )}
            <Input className="col-span-3 h-8" disabled={!editing} value={b.remark || ''} onChange={(e) => updateBand(i, 'remark', e.target.value)} />
            {editing && (
              <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8" onClick={() => removeBand(i)} data-testid={`grading-band-remove-${i}`}>
                ×
              </Button>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <Button size="sm" variant="outline" onClick={addBand} className="mt-2 h-8 text-xs" data-testid="grading-band-add">+ Add band</Button>
      )}
    </div>
  );
}

